"use client";

import { add, addDays, eachMonthOfInterval, endOfYear, format, getHours, isBefore, isEqual, isFuture, lastDayOfMonth, parse, startOfMonth, startOfToday, subDays, subMonths } from "date-fns";
import { zhCN } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { locales } from "@/i18n";
import { useLocale } from "next-intl";
import { buttonVariants } from "./button";
import { cn } from "@/lib/utils";
import { BEIJING_NOW_DATE } from "@/00-shared/const";
import { useDisabledDate } from "@/03-presenters/shared/hooks";
function getStartOfCurrentMonth() {
  return startOfMonth(startOfToday());
}
interface MonthPickerProps {
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
  isDashboard?: boolean;
}
export default function MonthPicker({
  currentMonth,
  onMonthChange,
  isDashboard
}: MonthPickerProps) {
  const disabledDate = useDisabledDate(isDashboard);
  const [currentYear, setCurrentYear] = React.useState(format(currentMonth, "yyyy"));
  const locale = useLocale() as locales;
  const firstDayCurrentYear = parse(currentYear, "yyyy", new Date());
  const months = eachMonthOfInterval({
    start: firstDayCurrentYear,
    end: endOfYear(firstDayCurrentYear)
  });
  function isInCurrentMonth(month: Date) {
    const lastDay = lastDayOfMonth(month);
    return isBefore(new Date(), lastDay);
  }
  function previousYear() {
    let firstDayNextYear = add(firstDayCurrentYear, {
      years: -1
    });
    setCurrentYear(format(firstDayNextYear, "yyyy"));
  }
  function nextYear() {
    let firstDayNextYear = add(firstDayCurrentYear, {
      years: 1
    });
    setCurrentYear(format(firstDayNextYear, "yyyy"));
  }
  function isFirstDayOfMonthBeforeThreePm(month: Date) {
    return format(BEIJING_NOW_DATE, "dd") === "01" && getHours(BEIJING_NOW_DATE) < 15 && format(month, "yyyyMMdd") === format(subMonths(BEIJING_NOW_DATE, 1), "yyyyMMdd");
  }
  return <div className="p-3" data-sentry-component="MonthPicker" data-sentry-source-file="month-picker.tsx">
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
        <div className="space-y-4">
          <div className="relative flex items-center justify-center pt-1">
            <div className="text-sm font-medium" aria-live="polite" role="presentation" id="month-picker">
              {format(firstDayCurrentYear, "yyyy")}
            </div>
            <div className="flex items-center space-x-1">
              <button name="previous-year" aria-label="Go to previous year" className={cn(buttonVariants({
              variant: "outline"
            }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100", "absolute left-1")} type="button" onClick={previousYear}>
                <ChevronLeft className="h-4 w-4" data-sentry-element="ChevronLeft" data-sentry-source-file="month-picker.tsx" />
              </button>
              <button name="next-year" aria-label="Go to next year" className={cn(buttonVariants({
              variant: "outline"
            }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100", "absolute right-1 disabled:bg-slate-100")} type="button" disabled={isFuture(add(firstDayCurrentYear, {
              years: 1
            }))} onClick={nextYear}>
                <ChevronRight className="h-4 w-4" data-sentry-element="ChevronRight" data-sentry-source-file="month-picker.tsx" />
              </button>
            </div>
          </div>
          <div className="grid w-full grid-cols-3 gap-2" role="grid" aria-labelledby="month-picker">
            {months.map(month => <div key={month.toString()} className="relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md dark:[&:has([aria-selected])]:bg-slate-800" role="presentation">
                <button name="day" className={cn("inline-flex h-9 w-16 items-center justify-center rounded-md p-0 text-sm font-normal ring-offset-white transition-colors aria-selected:opacity-100 hover:bg-accent hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:ring-offset-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-50 dark:focus-visible:ring-slate-800", isEqual(month, currentMonth) && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground", !isEqual(month, currentMonth) && isEqual(month, getStartOfCurrentMonth()) && "bg-accent text-accent-foreground dark:bg-slate-800 dark:text-slate-50")} disabled={isFuture(month) || isInCurrentMonth(month) || isBefore(month, subDays(disabledDate, 30)) || isFirstDayOfMonthBeforeThreePm(month)} role="gridcell" tabIndex={-1} type="button" onClick={() => onMonthChange(month)}>
                  <time dateTime={format(month, "yyyy-MM-dd")}>
                    {format(month, "MMM", {
                  locale: locale === "zh" ? zhCN : undefined
                })}
                  </time>
                </button>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}